import { AggregationAction, CollectionSchemaType } from '@next-space/fe-api-idl';
import { appI18n } from '@flowus/common/i18n';

export const AggregationActionUiMap = {
  none: appI18n.t('common.none'),
  countAll: appI18n.t('common.count'),
  countValue: appI18n.t('bitable.table_view.footer.aggregation.count_value'),
  countUniqueValue: appI18n.t('bitable.table_view.footer.aggregation.count_unique_value'),
  countEmpty: appI18n.t('bitable.table_view.footer.aggregation.count_empty'),
  countNotEmpty: appI18n.t('bitable.table_view.footer.aggregation.count_not_empty'),
  percentEmpty: appI18n.t('bitable.table_view.footer.aggregation.percent_empty'),
  percentNotEmpty: appI18n.t('bitable.table_view.footer.aggregation.percent_not_empty'),
  sum: appI18n.t('bitable.table_view.footer.aggregation.sum'),
  average: appI18n.t('bitable.table_view.footer.aggregation.average'),
  median: appI18n.t('bitable.table_view.footer.aggregation.median'),
  min: appI18n.t('bitable.table_view.footer.aggregation.min'),
  max: appI18n.t('bitable.table_view.footer.aggregation.max'),
  range: appI18n.t('bitable.table_view.footer.aggregation.range'),
  checked: appI18n.t('common.checked'),
  unchecked: appI18n.t('common.unchecked'),
  percentChecked: appI18n.t('bitable.table_view.footer.aggregation.percent_checked'),
  percentUnchecked: appI18n.t('bitable.table_view.footer.aggregation.percent_unchecked'),
  earliestDate: appI18n.t('bitable.table_view.footer.aggregation.earliest_date'),
  latestDate: appI18n.t('bitable.table_view.footer.aggregation.latest_date'),
  dateRange: appI18n.t('bitable.table_view.footer.aggregation.date_range'),
  showUnique: appI18n.t('common.show_unique'),
  showOrigin: appI18n.t('common.show_origin'),
  deduplication: appI18n.t('bitable.table_view.footer.aggregation.deduplication'),
};

export const commonAction = [
  { type: AggregationAction.NONE, title: AggregationActionUiMap.none },
  { type: AggregationAction.COUNT_ALL, title: AggregationActionUiMap.countAll },
  { type: AggregationAction.COUNT_VALUE, title: AggregationActionUiMap.countValue },
  { type: AggregationAction.COUNT_UNIQUE_VALUE, title: AggregationActionUiMap.countUniqueValue },
  { type: AggregationAction.COUNT_EMPTY, title: AggregationActionUiMap.countEmpty },
  { type: AggregationAction.COUNT_NOT_EMPTY, title: AggregationActionUiMap.countNotEmpty },
  { type: AggregationAction.PERCENT_EMPTY, title: AggregationActionUiMap.percentEmpty },
  { type: AggregationAction.PERCENT_NOT_EMPTY, title: AggregationActionUiMap.percentNotEmpty },
];

const dateAction = [
  ...commonAction,
  { type: AggregationAction.EARLIEST_DATE, title: AggregationActionUiMap.earliestDate },
  { type: AggregationAction.LATEST_DATE, title: AggregationActionUiMap.latestDate },
  { type: AggregationAction.DATE_RANGE, title: AggregationActionUiMap.dateRange },
];

const checkboxAction = [
  { type: AggregationAction.NONE, title: AggregationActionUiMap.none },
  { type: AggregationAction.COUNT_ALL, title: AggregationActionUiMap.countAll },
  { type: AggregationAction.CHECKED, title: AggregationActionUiMap.checked },
  { type: AggregationAction.UNCHECKED, title: AggregationActionUiMap.unchecked },
  { type: AggregationAction.PERCENT_CHECKED, title: AggregationActionUiMap.percentChecked },
  { type: AggregationAction.PERCENT_UNCHECKED, title: AggregationActionUiMap.percentUnchecked },
];

const numberAction = [
  ...commonAction,
  { type: AggregationAction.SUM, title: AggregationActionUiMap.sum },
  { type: AggregationAction.AVERAGE, title: AggregationActionUiMap.average },
  { type: AggregationAction.MEDIAN, title: AggregationActionUiMap.median },
  { type: AggregationAction.MIN, title: AggregationActionUiMap.min },
  { type: AggregationAction.MAX, title: AggregationActionUiMap.max },
  { type: AggregationAction.RANGE, title: AggregationActionUiMap.range },
];

export const MenuItems: Record<
  string,
  {
    type: AggregationAction;
    title: string;
  }[]
> = {
  [CollectionSchemaType.TITLE]: commonAction,
  [CollectionSchemaType.TEXT]: commonAction,
  [CollectionSchemaType.SELECT]: commonAction,
  [CollectionSchemaType.MULTI_SELECT]: commonAction,
  [CollectionSchemaType.NUMBER]: numberAction,
  [CollectionSchemaType.CHECKBOX]: checkboxAction,
  [CollectionSchemaType.DATE]: dateAction,
  [CollectionSchemaType.CREATED_AT]: dateAction,
  [CollectionSchemaType.UPDATED_AT]: dateAction,
  [CollectionSchemaType.CREATED_BY]: commonAction,
  [CollectionSchemaType.UPDATED_BY]: commonAction,
  [CollectionSchemaType.PERSON]: commonAction,
  [CollectionSchemaType.FILE]: commonAction,
  [CollectionSchemaType.URL]: commonAction,
  [CollectionSchemaType.EMAIL]: commonAction,
  [CollectionSchemaType.PHONE]: commonAction,
  [CollectionSchemaType.FORMULA]: commonAction,
  [CollectionSchemaType.RELATION]: commonAction,
  [CollectionSchemaType.ROLLUP]: commonAction,
};

export const getRollupAggregation = (
  type: CollectionSchemaType,
  aggregation?: AggregationAction
) => {
  if (isShowOriginalValue(aggregation)) {
    return commonAction;
  }

  if (
    (type === CollectionSchemaType.DATE ||
      type === CollectionSchemaType.CREATED_AT ||
      type === CollectionSchemaType.UPDATED_AT) &&
    isDateAggregation(aggregation)
  ) {
    return dateAction;
  }

  return numberAction;
};

export const isShowOriginalValue = (aggregation?: AggregationAction) => {
  return (
    !aggregation ||
    aggregation === AggregationAction.SHOW_UNIQUE ||
    aggregation === AggregationAction.SHOW_ORIGIN
  );
};

export const isDateAggregation = (aggregation?: AggregationAction) => {
  return (
    aggregation === AggregationAction.EARLIEST_DATE ||
    aggregation === AggregationAction.LATEST_DATE ||
    aggregation === AggregationAction.DATE_RANGE
  );
};

export const isNumberAggregation = (aggregation?: AggregationAction) => {
  return (
    aggregation === AggregationAction.SUM ||
    aggregation === AggregationAction.AVERAGE ||
    aggregation === AggregationAction.MEDIAN ||
    aggregation === AggregationAction.MIN ||
    aggregation === AggregationAction.MAX ||
    aggregation === AggregationAction.RANGE
  );
};

export const isPercentAggregation = (aggregation?: AggregationAction) => {
  return (
    aggregation === AggregationAction.PERCENT_EMPTY ||
    aggregation === AggregationAction.PERCENT_NOT_EMPTY ||
    aggregation === AggregationAction.PERCENT_CHECKED ||
    aggregation === AggregationAction.PERCENT_UNCHECKED
  );
};

export const getRollupValueType = (aggregation?: AggregationAction) => {
  if (isDateAggregation(aggregation)) {
    return 'date';
  }

  if (
    isNumberAggregation(aggregation) ||
    aggregation === AggregationAction.COUNT_ALL ||
    aggregation === AggregationAction.COUNT_VALUE ||
    aggregation === AggregationAction.COUNT_UNIQUE_VALUE ||
    aggregation === AggregationAction.COUNT_EMPTY ||
    aggregation === AggregationAction.COUNT_NOT_EMPTY ||
    aggregation === AggregationAction.CHECKED ||
    aggregation === AggregationAction.UNCHECKED
  ) {
    return 'number';
  }

  return 'string';
};
